import { useEffect, useState } from 'react';
import { Smartphone, MessageSquare, Send, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import ConnectionTab from './ConnectionTab';
import TemplatesTab from './TemplatesTab';
import DispatchTab from './DispatchTab';
import HistoryTab from './HistoryTab';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../../services/api';

type TabType = 'connection' | 'templates' | 'dispatch' | 'history';

export default function WhatsappPage() {
    const { isFreePlan } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<TabType>('connection');
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean | null>(null);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [checkingTerms, setCheckingTerms] = useState(true);

    const tabs = [
        { id: 'connection' as TabType, name: 'Conexão', icon: Smartphone },
        { id: 'templates' as TabType, name: 'Modelos de Mensagem', icon: MessageSquare },
        { id: 'dispatch' as TabType, name: 'Disparar Mensagens', icon: Send },
        { id: 'history' as TabType, name: 'Histórico', icon: Clock }
    ];

    useEffect(() => {
        if (isFreePlan) {
            navigate('/admin');
            toast.error('O recurso de WhatsApp não está disponível no plano Free.');
        }
    }, [isFreePlan, navigate]);

    useEffect(() => {
        checkTermsAcceptance();
    }, []);

    // Ler parâmetros da URL para abrir tab específica
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && ['connection', 'templates', 'dispatch', 'history'].includes(tab)) {
            setActiveTab(tab as TabType);
        }
    }, [searchParams]);

    const checkTermsAcceptance = async () => {
        try {
            setCheckingTerms(true);
            const response = await api.get('/whatsapp/terms/check');
            setHasAcceptedTerms(response.data.hasAccepted);

            if (!response.data.hasAccepted) {
                setShowTermsModal(true);
            }
        } catch (error: any) {
            console.error('Erro ao verificar aceite dos termos:', error);
            toast.error('Erro ao verificar aceite dos termos');
        } finally {
            setCheckingTerms(false);
        }
    };

    const handleOpenTerms = () => {
        // Abre em nova aba
        window.open('/admin/whatsapp-terms', '_blank');

        // Fecha o modal e começa a verificar periodicamente
        setShowTermsModal(false);

        // Verifica a cada 2 segundos se o usuário aceitou
        const intervalId = setInterval(async () => {
            try {
                const response = await api.get('/whatsapp/terms/check');
                if (response.data.hasAccepted) {
                    setHasAcceptedTerms(true);
                    clearInterval(intervalId);
                    toast.success('Termos aceitos! Você já pode usar a integração WhatsApp.');
                }
            } catch (error) {
                console.error('Erro ao verificar aceite:', error);
            }
        }, 2000);

        // Limpa o intervalo após 5 minutos
        setTimeout(() => {
            clearInterval(intervalId);
        }, 5 * 60 * 1000);
    };

    if (checkingTerms) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Verificando permissões...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Terms Modal */}
            {showTermsModal && !hasAcceptedTerms && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
                        <div className="flex items-center mb-4">
                            <AlertCircle className="h-8 w-8 text-yellow-500 mr-3" />
                            <h2 className="text-2xl font-bold text-gray-900">
                                Termos de Uso - WhatsApp
                            </h2>
                        </div>

                        <div className="space-y-4 text-gray-700">
                            <p>
                                Antes de começar a usar a integração com WhatsApp, precisamos que você
                                leia nossos <strong>Termos de Uso</strong>.
                            </p>

                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h3 className="font-semibold text-green-900 mb-2">
                                    ✨ Benefícios da Integração:
                                </h3>
                                <ul className="list-disc ml-5 space-y-1 text-sm text-green-800">
                                    <li>
                                        Envie mensagens personalizadas para seus clientes
                                    </li>
                                    <li>
                                        Crie templates reutilizáveis e agilize sua comunicação
                                    </li>
                                    <li>
                                        Automatize o contato com leads da roda de prêmios
                                    </li>
                                    <li>
                                        Acompanhe o histórico de todos os envios
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                                <div className="flex">
                                    <AlertCircle className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <p className="font-medium text-blue-800 mb-1">
                                            Importante saber:
                                        </p>
                                        <p className="text-blue-700">
                                            Esta integração funciona através de uma conexão direta com seu WhatsApp.
                                            Para garantir a melhor experiência, implementamos limites de envio
                                            (10 mensagens por disparo) e boas práticas de uso.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <p className="text-sm text-gray-600">
                                Os termos incluem informações sobre o funcionamento da integração e
                                responsabilidades de uso. Suas informações serão registradas para
                                fins de conformidade legal.
                            </p>
                        </div>

                        <div className="mt-6 flex space-x-3">
                            <button
                                onClick={handleOpenTerms}
                                className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <ExternalLink className="h-5 w-5 mr-2" />
                                Ler Termos e Começar
                            </button>
                            <button
                                onClick={() => navigate('/admin')}
                                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Voltar
                            </button>
                        </div>

                        <p className="text-xs text-gray-500 text-center mt-4">
                            Os termos serão abertos em uma nova aba para você ler com calma.
                            Após aceitar, você terá acesso imediato a todos os recursos!
                        </p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">WhatsApp</h1>
                <p className="mt-2 text-sm text-gray-700">
                    Gerencie sua conexão e envie mensagens em massa
                </p>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="border-b border-gray-200">
                    <nav className="flex -mb-px overflow-x-auto">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    disabled={!hasAcceptedTerms}
                                    className={`
                      group inline-flex items-center px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap
                      transition-colors
                      ${!hasAcceptedTerms ? 'opacity-50 cursor-not-allowed' : ''}
                      ${activeTab === tab.id
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }
                    `}
                                >
                                    <Icon
                                        className={`
                        -ml-0.5 mr-2 h-5 w-5
                        ${activeTab === tab.id
                                                ? 'text-blue-500'
                                                : 'text-gray-400 group-hover:text-gray-500'
                                            }
                      `}
                                    />
                                    {tab.name}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {!hasAcceptedTerms ? (
                        <div className="text-center py-12">
                            <AlertCircle className="h-16 w-16 text-blue-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Comece a usar o WhatsApp
                            </h3>
                            <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                Para começar a enviar mensagens personalizadas para seus clientes,
                                aceite nossos termos de uso.
                            </p>
                            <div className="space-y-4 flex flex-col items-center">
                                <button
                                    onClick={() => setShowTermsModal(true)}
                                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Ver Termos de Uso
                                </button>

                                <button
                                    className="mt-4 text-sm text-gray-500 underline"
                                    onClick={() => {
                                        window.location.reload();
                                    }}
                                >
                                    Já aceitei os termos, verificar novamente
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'connection' && <ConnectionTab />}
                            {activeTab === 'templates' && <TemplatesTab />}
                            {activeTab === 'dispatch' && <DispatchTab />}
                            {activeTab === 'history' && <HistoryTab />}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
